/* The credential, kept where a non-React module can reach it.
 *
 * The HTTP client needs the bearer token on every authenticated request, and
 * it is a plain function — it cannot call `useAuth()`. Passing the token down
 * through every call site instead would put an auth parameter on all forty-odd
 * API signatures to serve one header.
 *
 * So the token lives here: written by `state/auth.tsx` on sign-in, read
 * synchronously by `api/client.ts`, and cleared on sign-out or expiry. React
 * state stays the source of truth for *rendering*; this is the source of truth
 * for *transport*, and sign-in writes both in one place.
 *
 * localStorage, not memory, for the same reason the user object is stored: a
 * session that does not survive a refresh is a sign-in screen every time the
 * PWA is reopened. That choice accepts XSS-readable tokens — the mitigation is
 * a short expiry plus the CSP.
 *
 * The service has since settled that split rather than replacing it, so an
 * earlier note here — "if the Go service issues cookies instead, delete this
 * file" — no longer describes what happened. Both credentials are real and they
 * are deliberately different things: the *refresh* token is an HttpOnly,
 * SameSite=Strict, `__Host-` cookie that this file never sees and script cannot
 * read, and the *access* token is the fifteen-minute bearer stored here. Script
 * needs the second one to set a header; nothing needs the first. Deleting this
 * file would leave nothing to authenticate a request with.
 */

import { load, remove, save } from './storage'
import type { AuthSession } from './types'

const TOKEN_KEY = 'session'

interface StoredSession {
  token: string
  expiresAt: string
}

/** The live credential, or null when signed out or expired. */
export function currentToken(): string | null {
  const stored = load<StoredSession | null>(TOKEN_KEY, null)
  if (!stored?.token) return null
  /* An expired token is worse than no token: it produces a 401 the UI reports
     as an error rather than as "you have been signed out". */
  if (Date.parse(stored.expiresAt) <= Date.now()) {
    remove(TOKEN_KEY)
    return null
  }
  return stored.token
}

export function startSession(session: AuthSession): void {
  save<StoredSession>(TOKEN_KEY, { token: session.token, expiresAt: session.expiresAt })
}

/** Replaces the access token without touching the user.
 *
 *  A refresh renews the credential and nothing else — the person signed in did
 *  not change, so the React auth state has nothing to re-render and this
 *  deliberately does not go through `signIn`. Separate from `startSession`
 *  because that one is the boundary where a *different* user may arrive, and a
 *  refresh must never be able to swap who is signed in. */
export function renewSession(token: string, expiresAt: string): void {
  save<StoredSession>(TOKEN_KEY, { token, expiresAt })
}

export function endSession(): void {
  remove(TOKEN_KEY)
}
